import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';

interface Operator {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  shiftStatus: 'offline' | 'online' | 'break' | 'busy';
  shiftStartTime: string | null;
  shiftNotes: string;
  currentTickets: number;
  maxTickets: number;
}

interface ShiftStats {
  totalOperators: number;
  onlineOperators: number;
  breakOperators: number;
  busyOperators: number;
  offlineOperators: number;
}

const ShiftMonitoringPage: React.FC = () => {
  const { user } = useAuthStore();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [stats, setStats] = useState<ShiftStats>({
    totalOperators: 0,
    onlineOperators: 0,
    breakOperators: 0,
    busyOperators: 0,
    offlineOperators: 0
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    fetchData();
    
    // Функция для проверки активности пользователя
    const checkUserActivity = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      const fiveMinutes = 5 * 60 * 1000; // 5 минут
      
      // Если пользователь неактивен более 5 минут, не делаем запросы
      if (timeSinceLastActivity < fiveMinutes) {
        fetchData();
      } else {
        console.log('🔍 Пользователь неактивен, пропускаем обновление данных');
      }
    };
    
    // Автоматическое обновление каждые 2 минуты (120 секунд) вместо 30 секунд
    // Это снизит нагрузку на сервер и уменьшит вероятность ошибок 429
    intervalRef.current = setInterval(checkUserActivity, 120000);
    
    // Отслеживание активности пользователя
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    
    // Добавляем слушатели событий активности
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Удаляем слушатели событий
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Получаем список операторов
      const operatorsResponse = await api.get('/api/shifts/operators');
      
      if (operatorsResponse.data.success && Array.isArray(operatorsResponse.data.data)) {
        setOperators(operatorsResponse.data.data);
      } else {
        setOperators([]);
      }

      // Получаем статистику
      const statsResponse = await api.get('/api/shifts/stats');
      
      if (statsResponse.data.success && statsResponse.data.data) {
        setStats(statsResponse.data.data);
      }
    } catch (error) {
      console.error('Ошибка при получении данных:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleForceEndShift = async (operatorId: string, operatorName: string) => {
    const reason = prompt(`Укажите причину принудительного завершения смены для ${operatorName}:`);
    if (!reason) return;

    try {
      setActionLoading(operatorId);
      await api.post(`/api/shifts/${operatorId}/force-end`, { reason });
      await fetchData();
    } catch (error) {
      console.error('Ошибка при завершении смены:', error);
      alert('Ошибка при завершении смены');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-600 bg-green-100';
      case 'busy': return 'text-red-600 bg-red-100';
      case 'break': return 'text-yellow-600 bg-yellow-100';
      case 'offline': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online': return 'На смене';
      case 'busy': return 'Занят';
      case 'break': return 'Перерыв';
      case 'offline': return 'Не на смене';
      default: return 'Неизвестно';
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'superadmin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
          <strong>Доступ запрещен!</strong> Требуются права администратора для просмотра этой страницы.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
          <Skeleton className="h-4 w-48 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-foreground">Мониторинг смен</h1>
        <p className="text-muted-foreground">Администратор: {user?.firstName} {user?.lastName}</p>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-foreground">{stats.totalOperators}</div>
          <div className="text-sm text-muted-foreground">Всего операторов</div>
        </div>
        <div className="bg-success-500/10 border border-success-500/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-success-500">{stats.onlineOperators}</div>
          <div className="text-sm text-success-500">На смене</div>
        </div>
        <div className="bg-warning-500/10 border border-warning-500/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-warning-500">{stats.breakOperators}</div>
          <div className="text-sm text-warning-500">Перерыв</div>
        </div>
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-destructive">{stats.busyOperators}</div>
          <div className="text-sm text-destructive">Занят</div>
        </div>
        <div className="bg-muted border border-border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-muted-foreground">{stats.offlineOperators}</div>
          <div className="text-sm text-muted-foreground">Не на смене</div>
        </div>
      </div>

      {/* Список операторов */}
      <div className="bg-card border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Операторы</h2>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Оператор</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Время начала смены</TableHead>
                <TableHead>Заявки</TableHead>
                <TableHead>Заметки</TableHead>
                <TableHead>Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {operators.map((operator) => (
                <TableRow key={operator.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full flex items-center justify-center bg-muted">
                          <span className="text-sm font-medium text-foreground">
                            {operator.firstName.charAt(0)}{operator.lastName.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-foreground">
                          {operator.firstName} {operator.lastName}
                        </div>
                        <div className="text-sm text-muted-foreground">@{operator.username}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getStatusColor(operator.shiftStatus)}>
                      {getStatusText(operator.shiftStatus)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-foreground">
                    {operator.shiftStartTime ? 
                      new Date(operator.shiftStartTime).toLocaleString('ru-RU') : 
                      '-'
                    }
                  </TableCell>
                  <TableCell className="text-sm text-foreground">
                    {operator.currentTickets || 0} / {operator.maxTickets || 0}
                  </TableCell>
                  <TableCell className="text-sm max-w-xs truncate text-foreground">
                    {operator.shiftNotes || '-'}
                  </TableCell>
                  <TableCell>
                    {operator.shiftStatus !== 'offline' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleForceEndShift(operator.id, `${operator.firstName} ${operator.lastName}`)}
                        disabled={actionLoading === operator.id}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        {actionLoading === operator.id ? 'Завершение...' : 'Завершить смену'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Кнопка обновления */}
      <div className="mt-8 text-center">
        <Button
          onClick={fetchData}
          disabled={loading}
        >
          {loading ? 'Обновление...' : 'Обновить данные'}
        </Button>
        <div className="mt-2 text-sm text-muted-foreground">
          Данные автоматически обновляются каждые 2 минуты
        </div>
      </div>
    </div>
  );
};

export default ShiftMonitoringPage;